import { useMemo, useState } from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { axisDateLabel, pointChangeLabel, tooltipDateLabel } from '@/lib/chartDates';
import { formatCurrencyExact } from '@/lib/currency';
import { cn } from '@/lib/utils';

const METRICS = [
	{ key: 'impressions', label: 'Impressions' },
	{ key: 'clicks', label: 'Clicks' },
	{ key: 'ctr', label: 'CTR' },
	{ key: 'cpc', label: 'CPC' },
	{ key: 'spend', label: 'Spend' }
];

const safeNumber = (n) => (Number.isFinite(Number(n)) ? Number(n) : 0);

// Each metric points at a `{value, prev}` field pair on the per-day series
// the handler returns. CTR is derived per-day from impressions/clicks so the
// shape matches whether or not the handler emits a precomputed `ctr` column.
function transform(series, metric) {
	return series.map((d) => {
		const impressions = safeNumber(d.impressions);
		const clicks = safeNumber(d.clicks);
		const prevImpressions = safeNumber(d.prevImpressions);
		const prevClicks = safeNumber(d.prevClicks);
		let value;
		let prev;
		if (metric === 'impressions') {
			value = impressions;
			prev = prevImpressions;
		} else if (metric === 'clicks') {
			value = clicks;
			prev = prevClicks;
		} else if (metric === 'ctr') {
			value = impressions ? Number(((clicks / impressions) * 100).toFixed(2)) : 0;
			prev = prevImpressions ? Number(((prevClicks / prevImpressions) * 100).toFixed(2)) : 0;
		} else if (metric === 'cpc') {
			value = safeNumber(d.cpc);
			prev = safeNumber(d.prevCpc);
		} else {
			// Real per-day spend (the budget that was active that day), not a
			// flat constant. Honest to history even when the user changed their
			// daily budget mid-window.
			value = safeNumber(d.spend);
			prev = safeNumber(d.prevSpend);
		}
		// `prevDate` is the matching day in the previous window — the handler
		// aligns the two windows, so this is the only place that knows which
		// day the dashed line's value actually came from.
		return {
			date: d.date,
			prevDate: d.prevDate ?? null,
			value,
			prev,
			budget: safeNumber(d.spend),
			prevBudget: safeNumber(d.prevSpend)
		};
	});
}

const formatValue = (metric, value, currency) => {
	if (metric === 'ctr') return `${value.toFixed(2)}%`;
	if (metric === 'cpc' || metric === 'spend') return formatCurrencyExact(value, currency);
	return value.toLocaleString();
};

// A day with no budget behind it isn't a zero result, it's a day the ads weren't
// running — say so rather than showing "0 impressions", matching the
// retailers-dashboard tooltip.
const formatPoint = (metric, value, budget, currency) => (!budget && !value ? 'Off' : formatValue(metric, value, currency));

const metricLabel = (metric) => METRICS.find((m) => m.key === metric)?.label ?? 'Value';

/**
 * Names both days rather than saying "previous period" (NEA-6380) — the same
 * shape the retailers-dashboard tooltip uses, so the two products read alike.
 * Falls back to the old wording if the handler didn't send a `prevDate`.
 */
export function ChartTooltip({ active, payload, label, metric, currency }) {
	if (!active || !payload?.length) return null;
	const point = payload[0]?.payload;
	if (!point) return null;

	const change = point.budget && point.prevBudget ? pointChangeLabel(point.value, point.prev) : null;

	return (
		<div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs shadow-sm">
			<p className="font-semibold text-foreground">{metricLabel(metric)}</p>
			<p className="mt-1 text-primary">
				{tooltipDateLabel(point.date ?? label)}: {formatPoint(metric, point.value, point.budget, currency)}
				{change ? <span className="ml-1.5 font-semibold text-muted-foreground">{change}</span> : null}
			</p>
			<p className="text-muted-foreground">
				{point.prevDate ? tooltipDateLabel(point.prevDate) : 'Previous period'}:{' '}
				{formatPoint(metric, point.prev, point.prevBudget, currency)}
			</p>
		</div>
	);
}

export function AdsPerformanceChart({ series = [], currency }) {
	const [metric, setMetric] = useState('impressions');
	const data = useMemo(() => transform(series, metric), [series, metric]);

	return (
		<div>
			<div className="mb-5 flex flex-wrap gap-2">
				{METRICS.map((m) => (
					<button
						key={m.key}
						type="button"
						onClick={() => setMetric(m.key)}
						className={cn(
							'rounded-full px-3.5 py-1.5 text-xs font-semibold transition',
							metric === m.key ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground hover:bg-muted/70'
						)}
					>
						{m.label}
					</button>
				))}
			</div>
			<div className="h-64 w-full">
				<ResponsiveContainer width="100%" height="100%">
					<LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
						<CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
						{/*
						 * Ticks are formatted on the way out rather than baked into the
						 * data, so the tooltip still has the real date to work with.
						 * `preserveStartEnd` + a minimum gap keeps the labels readable
						 * whether the window is 7 days or 12 months — a fixed interval
						 * can't do both.
						 */}
						<XAxis
							dataKey="date"
							tickFormatter={axisDateLabel}
							tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
							tickLine={false}
							axisLine={false}
							interval="preserveStartEnd"
							minTickGap={28}
						/>
						<YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} width={40} />
						<Tooltip content={<ChartTooltip metric={metric} currency={currency} />} />
						<Line
							type="monotone"
							dataKey="prev"
							stroke="hsl(var(--muted-foreground))"
							strokeWidth={1.5}
							strokeDasharray="4 4"
							dot={false}
							name="Previous period"
						/>
						<Line
							type="monotone"
							dataKey="value"
							stroke="hsl(var(--primary))"
							strokeWidth={2.5}
							dot={false}
							name="This period"
						/>
					</LineChart>
				</ResponsiveContainer>
			</div>
		</div>
	);
}
